import React, { useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cn from 'classnames';

import {
	Checkbox,
	GroupSelectOptionsInterface,
	IconButton,
	Icons,
	SearchInput,
	SubmissionButtons,
} from '@components/common';

import { generateOptions, generateOptionsByIds } from '@utils/groupSelectUtils';
import {
	areAllItemsChecked,
	filterHiddenOptions,
	sortByChecked,
	sortByName,
} from '@utils/sortGroupSelectOptionsUtils/sortGroupSelectOptionsUtils';

import { IGroupSelectMenuProps } from './GroupSelectMenu.types';
import { IGroupSelectItem } from '@components/common/GroupSelect/GroupSelect.types';

import {
	CATEGORY_HEIGHT,
	ITEM_GAP,
	ITEM_HEIGHT,
	PADDING_BOTTOM_VIRTUALIZATION_LIST,
} from '@components/common/GroupSelect/constants';

import VirtualizedRow from './VirtualizedRow';

import './GroupSelectMenu.scss';

const GroupSelectMenu: React.FC<IGroupSelectMenuProps> = ({
	dynamicOptions,
	placeholder,
	searchThreshold,
	onSave,
	toggleSelectMenu,
	isAllItemsSelected,
	selectAllName,
	iSortByChecked,
	withVirtualization,
	readOnly,
	maxSelectionNumber,
	maxSelectionText,
}) => {
	const listRef = useRef();

	const [isCheckedSort, setIsCheckedSort] = useState(iSortByChecked);
	const [isAllItemsInMenuSelected, setIsAllItemsInMenuSelected] = useState(isAllItemsSelected);
	const [itemsCheckedCount, setItemsCheckedCount] = useState(0);

	const [dynamicMenuOptions, setDynamicMenuOptions] = useState(sortByChecked(dynamicOptions));
	const [searchValue, setSearchValue] = useState('');

	const dynamicMenuOptionsForVirtualization = useMemo(() => {
		return filterHiddenOptions(dynamicMenuOptions);
	}, [dynamicMenuOptions]);

	useEffect(() => {
		const allChecked = dynamicMenuOptions.every(option => areAllItemsChecked(option.items));

		const itemsCheckedCount = dynamicMenuOptions.reduce((acc, next) => {
			const rowItemsCheckedCount = next.items.filter(item => item.checked).length;

			return acc + rowItemsCheckedCount;
		}, 0);

		setItemsCheckedCount(itemsCheckedCount);
		setIsAllItemsInMenuSelected(allChecked);
	}, [dynamicOptions, dynamicMenuOptions]);

	useEffect(() => onSearchHandler(''), []);

	const onChangeSort = () => {
		setIsCheckedSort(prevState => {
			if (!prevState) {
				setDynamicMenuOptions(prevState => sortByChecked(prevState));
			} else setDynamicMenuOptions(prevState => sortByName(prevState));

			return !prevState;
		});
	};

	const refreshOptions = () => setDynamicMenuOptions(prevState => sortByChecked(prevState));

	const [collapsibleBlocksState, setCollapsibleBlocksState] = useState<Record<string, boolean>>({});

	const toggleCollapsibleBlocks = (name: string) => {
		setCollapsibleBlocksState(prevState => {
			return {
				...prevState,
				[name]: !prevState[name],
			};
		});

		if (withVirtualization) repaintAllList();
	};

	const onChangeHandler = (isChecked: boolean, ids: Array<string>, categoryName: string) =>
		setDynamicMenuOptions(generateOptionsByIds(dynamicMenuOptions, !isChecked, ids, categoryName));

	const onSearchHandler = (value: string) => {
		const isEmptyValue = !value;
		const lowercaseValue = value.toLowerCase();

		setSearchValue(value);

		const filterItems = (items: Array<IGroupSelectItem>, categoryMatch: boolean): Array<IGroupSelectItem> => {
			return items.map(item => {
				if (item.items && item.items.length > 0) {
					const nestedItems = filterItems(item.items, categoryMatch
						|| (item.category?.title?.toLowerCase().includes(lowercaseValue) ?? false));
					const isAnyNestedVisible = nestedItems.some(nestedItem => !nestedItem.isHidden);

					return {
						...item,
						isHidden: !isAnyNestedVisible && !categoryMatch
							&& !item.title?.toLowerCase().includes(lowercaseValue),
						items: nestedItems,
					};
				}

				const isItemMatch = item.title?.toLowerCase().includes(lowercaseValue) ?? false;

				return {
					...item,
					isHidden: !isItemMatch && !categoryMatch,
				};
			});
		};

		setDynamicMenuOptions(prevState => prevState.map(option => {
			const isCategoryMatch = option.category.title.toLowerCase().includes(lowercaseValue);
			const filteredItems = filterItems(option.items, isCategoryMatch);

			const isAnyItemVisible = filteredItems.some(item => !item.isHidden);
			const isHiddenCategory = !(isEmptyValue || isAnyItemVisible || isCategoryMatch);

			return {
				category: {
					...option.category,
					isHidden: isHiddenCategory,
				},
				items: filteredItems,
			};
		}));
	};

	const toggleAllCheckbox = () => {
		setDynamicMenuOptions(generateOptions(dynamicMenuOptions, !isAllItemsInMenuSelected));
	};

	const onResetHandler = () => {
		setDynamicMenuOptions(dynamicOptions);
		onSearchHandler('');
	};

	const onSaveHandler = () => {
		onSave(dynamicMenuOptions);
		toggleSelectMenu();
	};

	const onSearchClear = () => {
		if (!isCheckedSort) return;

		refreshOptions();
	};

	const repaintAllList = () => {
		if (listRef?.current) {
			dynamicMenuOptionsForVirtualization.forEach((_: any, index: number) => {
				(listRef.current as any)?.resetAfterIndex(index, true);
			});
		}
	};

	useEffect(() => {
		if (withVirtualization) repaintAllList();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dynamicMenuOptions]);

	const getItemSize = (index: number, data: Array<GroupSelectOptionsInterface>) => {
		const option = data[index];
		const isOpen = collapsibleBlocksState[option.category.name];

		const calculateItemsSize = (items: Array<IGroupSelectItem>, isParentOpen: boolean): number => {
			return items.reduce((acc, item) => {
				const isItemOpen = (item.category?.name && collapsibleBlocksState[item.category.name]) && isParentOpen;

				if (item.items && isItemOpen) {
					return acc + ITEM_HEIGHT + ITEM_GAP + calculateItemsSize(item.items, isItemOpen);
				}

				return acc + (isParentOpen ? ITEM_HEIGHT + ITEM_GAP : 0);
			}, 0);
		};

		const itemsSize = calculateItemsSize(option.items, isOpen);

		return isOpen ? CATEGORY_HEIGHT + itemsSize : CATEGORY_HEIGHT;
	};

	const isUncheckedDisabled = !!maxSelectionNumber && itemsCheckedCount >= maxSelectionNumber;

	const isMaxSelectionError = !!maxSelectionNumber && itemsCheckedCount > maxSelectionNumber;

	return (
		<div
			className='f5gGroupSelectMenuContainer'
			data-testid='group-select-menu'
		>
			<div className='f5gGroupSelectMenuSearch'>
				<div className={cn({ 'f5gGroupMenuSearchHeader': maxSelectionNumber && maxSelectionText })}>
					{maxSelectionNumber && maxSelectionText
						&&
						<div className={cn('f5gGroupMenuMaxSelectionText', { 'f5gGroupMenuMaxSelectionTextError': isMaxSelectionError })}>
							{maxSelectionText}
						</div>
					}
					<div className='f5gGroupSortButtons'>
						<IconButton
							icon={Icons.resetIcon}
							isDisabled={!isCheckedSort || readOnly}
							isFillPath
							isRound
							isWithoutShadow
							onClick={refreshOptions}
							tooltipLabel='Refresh'
						/>
						<IconButton
							icon={Icons.alphabeticSortIcon}
							isActive={!isCheckedSort}
							isRound
							isWithoutShadow
							onClick={onChangeSort}
							tooltipLabel={isCheckedSort ? 'Sort alphabetically' : 'Set default sorting'}
						/>
					</div>
				</div>
				<SearchInput
					isClearable
					onClear={onSearchClear}
					onSearch={onSearchHandler}
					placeholder={placeholder}
					searchThreshold={searchThreshold}
					value={searchValue}
				/>
			</div>
			<div className={cn('f5gGroupSelectMenuOptions', { 'withoutVirtualization': !withVirtualization })}>
				{!maxSelectionNumber
					&& <div className={cn('f5gGroupSelectCheckboxOption')}>
						<Checkbox
							checked={isAllItemsInMenuSelected}
							disabled={readOnly}
							isPropagation
							label='All'
							name={selectAllName}
							onChange={toggleAllCheckbox}
						/>
					</div>
				}
				{withVirtualization
					? <AutoSizer>
						{({ height = 0, width = 0 }) => (
							<List
								ref={listRef as any}
								height={height - PADDING_BOTTOM_VIRTUALIZATION_LIST}
								itemCount={dynamicMenuOptionsForVirtualization.length}
								itemData={dynamicMenuOptionsForVirtualization}
								itemSize={(index: number) => getItemSize(index, dynamicMenuOptionsForVirtualization)}
								width={width}
							>
								{({ data, style, index }) => VirtualizedRow({
									collapsibleBlocksState,
									onChangeHandler,
									toggleCollapsibleBlocks,
									option: data[index],
									style,
									index,
									isOverflow: true,
									readOnly,
								})}
							</List>
						)}
					</AutoSizer>
					: dynamicMenuOptions.map((option, index) => {
						return (
							<VirtualizedRow
								key={`${index + option.category.name}`}
								collapsibleBlocksState={collapsibleBlocksState}
								index={index}
								isUncheckedDisabled={isUncheckedDisabled}
								onChangeHandler={onChangeHandler}
								option={option}
								readOnly={readOnly}
								toggleCollapsibleBlocks={toggleCollapsibleBlocks}
							/>
						);
					})
				}
			</div>
			{!readOnly && (
				<div className='f5gGroupSelectMenuButtons'>
					<SubmissionButtons
						disabledPositive={isMaxSelectionError}
						negativeHandler={onResetHandler}
						negativeLabel='Revert changes'
						position='right'
						positiveHandler={onSaveHandler}
						positiveLabel='Save'
					/>
				</div>
			)}
		</div>
	);
};

export default GroupSelectMenu;
